<section class="exam-summary-shell min-h-screen bg-slate-50 px-5 py-5 text-text-primary sm:px-7 lg:px-10">
  <div class="mx-auto w-full">

    @if (isLoading()) {
      <div class="flex min-h-[60vh] items-center justify-center">
        <div class="loading-spinner"></div>
      </div>
    } @else if (hasError()) {
      <div class="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <p class="text-lg font-semibold text-slate-600 font-inter">Unable to load exam result</p>
        <p class="text-sm text-slate-400 font-inter">Please try again later.</p>
        <div class="mt-2 w-48">
          <app-primary-button
            text="Back to Dashboard"
            ariaLabel="Back to dashboard"
            buttonClass="w-full rounded-xl py-3 text-base font-semibold tracking-normal"
            (buttonClick)="onBackToDashboard()" />
        </div>
      </div>
    } @else {
      <header class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          @if (canGoBack()) {
            <button
              type="button"
              class="exam-summary-back-button"
              aria-label="Back to exam review"
              (click)="onReview()">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-4 w-4">
                <path d="M11.667 5L6.667 10L11.667 15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          }

          <div>
            <h1 class="sub-heading font-semibold">
              Exam Summary
              @if (examResultState.examTitle) {
                <span class="text-text-secondary">- {{ examResultState.examTitle }}</span>
              }
            </h1>
            <p class="secondary-label text-text-secondary mt-1">
              {{ examResultState.examMeta }}
            </p>
          </div>
        </div>
      </header>

      <section class="summary-hero mt-3 overflow-hidden rounded-xl px-5 py-7 text-white sm:px-8 sm:py-8 lg:px-10">
        <div class="mx-auto flex max-w-4xl flex-col items-center text-center">
          <p class="text-base text-white/80 uppercase font-semibold font-inter">TOTAL SCORE</p>
          <h2 class="text-3xl font-medium sm:text-5xl sm:font-extrabold text-white">{{ scoreText }}</h2>
          <div class="flex flex-wrap justify-center items-center gap-2">
            <span [class]="performanceChipClass">{{ examResultState.performanceLabel }}</span>
            @if (examResultState.cutoffMarks !== undefined && examResultState.cutoffMarks !== null) {
              <span class="summary-hero-chip bg-white/10 text-white border border-white/20 backdrop-blur-sm">
                Cutoff: {{ examResultState.cutoffMarks }}
              </span>
            }
          </div>

          <div class="mt-6 flex flex-wrap justify-center gap-3">
            @for (metric of metrics; track metric.id) {
              <article [class]="getMetricClass(metric.tone)">
                <p class="px-3 py-1.5">{{ metric.label }}: {{ metric.value }}</p>
              </article>
            }
          </div>
        </div>
      </section>

      <section class="mt-6">
        <div class="border-b border-slate-200 pb-2">
          <h2 class="exam-summary-section-title font-inter">Section-wise Performance</h2>
        </div>

        <div class="summary-table-scroll mt-5 overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div class="summary-table">
        <div class="summary-table-grid hidden bg-slate-100 px-6 py-4 md:grid">
            <p class="table-head font-inter">Section</p>
            <p class="table-head font-inter">Total Qs</p>
            <p class="table-head font-inter">Not Visited</p>
            <p class="table-head font-inter">Answered</p>
            <p class="table-head font-inter">Skipped</p>
            <p class="table-head font-inter">Correct</p>
            <p class="table-head font-inter">Wrong</p>
            <p class="table-head font-inter">Marked for Review</p>
            <p class="table-head font-inter">Guess</p>
            <p class="table-head font-inter">Correct Guess</p>
            <p class="table-head font-inter">Marks Obtained</p>
            <p class="table-head font-inter inline-flex items-center gap-1.5">
              Accuracy
              <span class="info-tooltip" tabindex="0" role="button" aria-label="How accuracy is calculated" aria-describedby="accuracy-formula"
                (mouseenter)="showAccuracyTip($event)" (focus)="showAccuracyTip($event)"
                (mouseleave)="accuracyTipVisible.set(false)" (blur)="accuracyTipVisible.set(false)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4" aria-hidden="true">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
                </svg>
              </span>
            </p>
          </div>

          <div class="divide-y divide-slate-100">
            @for (section of sections; track section.id) {
              <article class="summary-table-grid grid gap-3 px-4 py-3.5 md:items-center md:px-6 md:py-4">
                <div class="flex items-center gap-3">
                  <span [class]="getBadgeClass(section.badgeTone)">{{ section.badge }}</span>
                  <div>
                    <p class="table-section-name font-inter">{{ section.label }}</p>
                    <p class="table-mobile-subtitle font-inter md:hidden">
                      {{ section.totalQuestions }} questions
                    </p>
                  </div>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Total Qs</span>
                  <span class="table-value font-inter">{{ section.totalQuestions }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Not Visited</span>
                  <span class="table-value table-value-muted font-inter">{{ section.notVisited ?? 0 }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Answered</span>
                  <span class="table-value table-value-green font-inter">{{ section.answered }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Skipped</span>
                  <span class="table-value table-value-muted font-inter">{{ section.unanswered }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Correct</span>
                  <span class="table-value table-value-green font-inter">{{ section.correct ?? 0 }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Wrong</span>
                  <span class="table-value table-value-red font-inter">{{ section.wrong ?? 0 }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Marked for Review</span>
                  <span class="table-value table-value-violet font-inter">{{ section.markedForReview }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Guess</span>
                  <span class="table-value table-value-amber font-inter">{{ section.guess ?? 0 }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Correct Guess</span>
                  <span class="table-value table-value-green font-inter">{{ section.correctGuess ?? 0 }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Marks Obtained</span>
                  <span class="table-value table-value-indigo font-inter">{{ section.marksObtained }}</span>
                </div>

                <div class="table-cell-pair">
                  <span class="table-mobile-label font-inter md:hidden">Accuracy</span>
                  <div class="w-full">
                    <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        class="accuracy-bar h-full rounded-full"
                        [style.width]="getProgressWidth(section.accuracy)"></div>
                    </div>
                    <span class="table-value table-value-muted mt-2 inline-block font-inter">{{ section.accuracy }}%</span>
                  </div>
                </div>
              </article>
            }
          </div>
        </div>
        </div>

        <!-- Accuracy formula tooltip (fixed so the scrolling table doesn't clip it) -->
        <span class="info-tooltip-content" role="tooltip" id="accuracy-formula"
          [class.is-visible]="accuracyTipVisible()"
          [style.top.px]="accuracyTip()?.top"
          [style.right.px]="accuracyTip()?.right">
          <span class="block font-semibold text-white mb-1.5">How is this calculated?</span>
          <code class="info-tooltip-formula">Accuracy = (Correct ÷ Answered) × 100</code>
        </span>
      </section>

      <div class="mt-5 flex flex-col gap-4 sm:flex-row sm:justify-end">
        <div class="w-full sm:w-60">
          <app-outline-button
            text="Back to Dashboard"
            ariaLabel="Back to dashboard"
            buttonClass="w-full rounded-xl py-3 text-base font-semibold tracking-normal"
            (buttonClick)="onBackToDashboard()" />
        </div>

        <div class="w-full sm:w-60">
          <app-primary-button
            text="Review"
            ariaLabel="Review answers"
            buttonClass="w-full rounded-xl py-3 text-base font-semibold tracking-normal"
            (buttonClick)="onReview()" />
        </div>
      </div>
    }
  </div>
</section>
